import React, { useState, useEffect, useRef } from 'react';
import {
  Search, Send, AlertCircle, Clock, CheckCircle, ChevronLeft, ChevronRight, User, Info, Check, X
} from 'lucide-react';
import { adminService } from '../services/adminService';
import toast from 'react-hot-toast';
import { io } from 'socket.io-client';

const PAGE_SIZE = 10;
const SOCKET_URL = 'https://apitest.pairpinnacle.com';

const categoryColors = {
  payment: 'bg-blue-100 text-blue-800',
  account: 'bg-purple-100 text-purple-800',
  technical: 'bg-orange-100 text-orange-800',
  general: 'bg-gray-100 text-gray-800',
};

const statusColors = {
  open: 'bg-yellow-100 text-yellow-800',
  in_progress: 'bg-blue-100 text-blue-800',
  resolved: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-800',
};

const Support = () => {
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [search, setSearch] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedPriority, setSelectedPriority] = useState('all');
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [socketConnected, setSocketConnected] = useState(false);
  const [unreadTickets, setUnreadTickets] = useState({});
  const [messageIds, setMessageIds] = useState(new Set());
  const [userDetails, setUserDetails] = useState(null);
  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);
  const selectedTicketRef = useRef(null);

  // Keep selectedTicketRef in sync with selectedTicket
  useEffect(() => {
    selectedTicketRef.current = selectedTicket;
  }, [selectedTicket]);

  // Auto-select first ticket after loading
  useEffect(() => {
    fetchTickets();
    
    // Initialize socket connection
    socketRef.current = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      timeout: 10000,
    });

    socketRef.current.on('connect', () => {
      console.log('📡 Admin socket connected');
      setSocketConnected(true);
      // Join admin global room for new ticket notifications
      socketRef.current.emit('join-admin');
    });

    socketRef.current.on('disconnect', () => {
      console.log('📡 Admin socket disconnected');
      setSocketConnected(false);
    });

    // Listen for new tickets globally
    socketRef.current.on('new-ticket', (data) => {
      console.log('🎫 New ticket received:', data);
      if (data.ticket) {
        setTickets(prev => [data.ticket, ...prev]);
        toast.success(`New ticket #${data.ticket.id} created!`);
      }
    });

    // Listen for new messages globally (for tickets not currently selected)
    socketRef.current.on('new-message', (msg) => {
      console.log('📨 Admin received new message:', msg);
      
      // Process message to ensure is_admin is properly set
      const processedMsg = {
        ...msg,
        is_admin: msg.is_admin || msg.sender === 'admin'
      };
      
      // Use ref to get current selectedTicket value
      const currentSelectedTicket = selectedTicketRef.current;
      
      // If message is for the currently selected ticket, add it to messages
      if (currentSelectedTicket && msg.ticket_id === currentSelectedTicket.id) {
        setMessages(prev => {
          // Check if message already exists
          if (prev.some(m => m.id === processedMsg.id)) {
            console.log(`📨 Message ${processedMsg.id} already exists, skipping`);
            return prev;
          }
          
          console.log(`📨 Adding new message ${processedMsg.id} to selected ticket ${currentSelectedTicket.id}`);
          const updated = [...prev, processedMsg];
          // Sort by created_at
          updated.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
          return updated;
        });
        
        // Show notification for user messages only (not admin messages)
        if (!processedMsg.is_admin && processedMsg.sender !== 'admin') {
          toast.success('New user message received!');
        }
      } else {
        // Mark ticket as having unread messages for other tickets
        setUnreadTickets(prev => ({ ...prev, [msg.ticket_id]: true }));
        
        if (!processedMsg.is_admin && processedMsg.sender !== 'admin') {
          toast.success(`New message in ticket #${msg.ticket_id}!`);
        }
      }
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.emit('leave-admin');
        socketRef.current.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    if (!selectedTicket && tickets.length > 0) {
      setSelectedTicket(tickets[0]);
    }
  }, [tickets]);

  const fetchTickets = async () => {
    setLoadingTickets(true);
    try {
      const data = await adminService.getSupportTickets();
      setTickets(Array.isArray(data) ? data : []);
    } catch {
      setTickets([]);
      toast.error('Failed to fetch tickets');
    } finally {
      setLoadingTickets(false);
    }
  };

  const fetchMessages = async (ticketId) => {
    setLoadingMessages(true);
    try {
      const data = await adminService.getSupportTicket(ticketId);
      // Use 'messages' if that's what your API returns
      const replies = data.messages || data.replies || [];
      // Process messages to ensure is_admin is properly set
      const processedReplies = replies.map(msg => ({
        ...msg,
        is_admin: msg.is_admin || msg.sender === 'admin' // Ensure consistent admin detection
      }));
      setMessages(processedReplies);
      setMessageIds(new Set(processedReplies.map(m => m.id)));
    } catch {
      setMessages([]);
      setMessageIds(new Set());
      toast.error('Failed to fetch messages');
    } finally {
      setLoadingMessages(false);
    }
  };

  useEffect(() => {
    if (selectedTicket) {
      fetchMessages(selectedTicket.id);

      // Join ticket room for real-time updates
      console.log(`🔗 Admin joining ticket room: ${selectedTicket.id}`);
      socketRef.current?.emit('join-ticket', selectedTicket.id);

      // No need for additional listeners here - the global listener handles everything

      return () => {
        console.log(`🚪 Admin leaving ticket room: ${selectedTicket.id}`);
        socketRef.current?.emit('leave-ticket', selectedTicket.id);
      };
    }
  }, [selectedTicket]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
    // Mark ticket as read when viewing
    if (selectedTicket) {
      setUnreadTickets(prev => ({ ...prev, [selectedTicket.id]: false }));
    }
  }, [messages, selectedTicket]);

  const handleSend = async () => {
    if (!reply.trim() || !selectedTicket) return;
    
    const messageText = reply.trim();
    setReply(''); // Clear input immediately for better UX
    
    try {
      console.log(`📤 Admin sending reply to ticket ${selectedTicket.id}:`, messageText);
      await adminService.replyToTicket(selectedTicket.id, messageText);
      // Message will be added by socket event, no need to fetch again
      toast.success('Reply sent!');
    } catch (error) {
      console.error('❌ Failed to send reply:', error);
      toast.error('Failed to send reply');
      setReply(messageText); // Restore message if failed
    }
  };

  const handleStatusChange = async (e) => {
    const newStatus = e.target.value;
    try {
      await adminService.updateTicketStatus(selectedTicket.id, newStatus);
      toast.success('Status updated!');
      setSelectedTicket({ ...selectedTicket, status: newStatus });
      fetchTickets();
    } catch {
      toast.error('Failed to update status');
    }
  };

  // Stats
  const openCount = tickets.filter(t => t.status === 'open').length;
  const inProgressCount = tickets.filter(t => t.status === 'in_progress').length;
  const resolvedCount = tickets.filter(t => t.status === 'resolved').length;
  const highPriorityCount = tickets.filter(t => t.priority === 'high' || t.priority === 'urgent').length;

  // Filter tickets by search, status, priority
  const filteredTickets = tickets.filter(
    t =>
      ((t.subject || '').toLowerCase().includes(search.toLowerCase()) ||
        (t.user_name || '').toLowerCase().includes(search.toLowerCase()) ||
        (t.user_email || '').toLowerCase().includes(search.toLowerCase()) ||
        (t.user_phone || '').toLowerCase().includes(search.toLowerCase())) &&
      (selectedStatus === 'all' || t.status === selectedStatus) &&
      (selectedPriority === 'all' || t.priority === selectedPriority)
  );
  const totalPages = Math.ceil(filteredTickets.length / PAGE_SIZE);
  const paginatedTickets = filteredTickets.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Helper for category badge
  const getCategoryBadge = (category) => (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${categoryColors[category] || 'bg-gray-100 text-gray-800'}`}>
      {category || 'General'}
    </span>
  );

  // Helper for status badge
  const getStatusBadge = (status) => (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
      {status?.replace('_', ' ') || 'Unknown'}
    </span>
  );

  // Helper for avatar
  const getAvatar = (msg) => (
    <div className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-300 mr-2 border border-gray-400">
      {msg.is_admin ? <User className="h-5 w-5 text-green-700" /> : msg.sender === 'bot' ? <Info className="h-5 w-5 text-blue-700" /> : <User className="h-5 w-5 text-gray-700" />}
    </div>
  );

  // Fetch user details when a ticket is selected
  useEffect(() => {
    const fetchUserDetails = async () => {
      if (selectedTicket && selectedTicket.user_id) {
        try {
          const user = await adminService.getUserDetails(selectedTicket.user_id);
          setUserDetails(user);
        } catch {
          setUserDetails(null);
        }
      } else {
        setUserDetails(null);
      }
    };
    fetchUserDetails();
  }, [selectedTicket]);

  const user = selectedTicket?.user || userDetails; // <-- Add this line

  return (
    <div className="flex flex-col h-[80vh] bg-white rounded-lg shadow overflow-hidden">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 p-6 bg-gray-50 border-b">
        <div className="card p-4 flex items-center rounded-lg shadow-sm bg-white">
          <div className="p-3 rounded-full bg-yellow-100">
            <AlertCircle className="h-6 w-6 text-yellow-600" />
          </div>
          <div className="ml-4">
            <p className="text-sm font-medium text-gray-600">Open</p>
            <p className="text-2xl font-bold text-yellow-600">{openCount}</p>
          </div>
        </div>
        <div className="card p-4 flex items-center rounded-lg shadow-sm bg-white">
          <div className="p-3 rounded-full bg-blue-100">
            <Clock className="h-6 w-6 text-blue-600" />
          </div>
          <div className="ml-4">
            <p className="text-sm font-medium text-gray-600">In Progress</p>
            <p className="text-2xl font-bold text-blue-600">{inProgressCount}</p>
          </div>
        </div>
        <div className="card p-4 flex items-center rounded-lg shadow-sm bg-white">
          <div className="p-3 rounded-full bg-green-100">
            <CheckCircle className="h-6 w-6 text-green-600" />
          </div>
          <div className="ml-4">
            <p className="text-sm font-medium text-gray-600">Resolved</p>
            <p className="text-2xl font-bold text-green-600">{resolvedCount}</p>
          </div>
        </div>
        <div className="card p-4 flex items-center rounded-lg shadow-sm bg-white">
          <div className="p-3 rounded-full bg-red-100">
            <AlertCircle className="h-6 w-6 text-red-600" />
          </div>
          <div className="ml-4">
            <p className="text-sm font-medium text-gray-600">High Priority</p>
            <p className="text-2xl font-bold text-red-600">{highPriorityCount}</p>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="p-4 border-b bg-white flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search tickets by subject, user name, phone, or email..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="input-field pl-10 rounded-lg border border-gray-300"
            />
          </div>
        </div>
        <select
          value={selectedStatus}
          onChange={e => setSelectedStatus(e.target.value)}
          className="input-field rounded-lg border border-gray-300"
        >
          <option value="all">All Status</option>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>
        <select
          value={selectedPriority}
          onChange={e => setSelectedPriority(e.target.value)}
          className="input-field rounded-lg border border-gray-300"
        >
          <option value="all">All Priority</option>
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
          <option value="urgent">Urgent</option>
        </select>
      </div>

      {/* Main Content: Sidebar + Chat + Details */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <div className="w-80 border-r bg-gray-50 flex flex-col">
          <div className="flex-1 overflow-y-auto">
            {loadingTickets ? (
              <div className="flex items-center justify-center h-full">
                <span className="text-gray-400">Loading...</span>
              </div>
            ) : (
              paginatedTickets.map(ticket => (
                <div
                  key={ticket.id}
                  className={`p-4 border-b cursor-pointer hover:bg-blue-50 transition-all ${selectedTicket?.id === ticket.id ? 'bg-blue-100' : ''}`}
                  onClick={() => setSelectedTicket(ticket)}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-blue-700">#{ticket.id}</span>
                    {getCategoryBadge(ticket.category)}
                    {unreadTickets[ticket.id] && (
                      <span className="ml-2 px-2 py-0.5 rounded-full bg-red-500 text-white text-xs animate-pulse">NEW</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500">
                    {ticket.user_name || 'User'} • {ticket.status}
                  </div>
                  <div className="text-xs text-gray-400">
                    {ticket.user_phone || 'No phone'} • {ticket.created_at ? new Date(ticket.created_at).toLocaleDateString() : ''}
                  </div>
                </div>
              ))
            )}
          </div>
          {/* Pagination */}
          {!loadingTickets && filteredTickets.length > PAGE_SIZE && (
            <div className="p-2 border-t flex items-center justify-between bg-gray-50">
              <button
                className="btn-secondary"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                <ChevronLeft className="h-4 w-4" /> Prev
              </button>
              <span className="text-xs text-gray-600">
                Page {currentPage} of {totalPages}
              </span>
              <button
                className="btn-secondary"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>

        {/* Chat Window */}
        <div className="flex-1 flex flex-col bg-white">
          {/* Header */}
          <div className="p-4 border-b flex items-center justify-between bg-white">
            {selectedTicket ? (
              <>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-blue-700">#{selectedTicket.id}</span>
                    {getCategoryBadge(selectedTicket.category)}
                    {getStatusBadge(selectedTicket.status)}
                  </div>
                  <div className="text-xs text-gray-500">
                    {selectedTicket.user_name} ({selectedTicket.user_email}) • {selectedTicket.user_phone}
                  </div>
                  <div className="text-xs text-gray-400">
                    Created: {selectedTicket.created_at ? new Date(selectedTicket.created_at).toLocaleString() : ''}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={selectedTicket.status}
                    onChange={handleStatusChange}
                    className="input-field text-xs rounded-lg border border-gray-300"
                  >
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>
                  <span className={`ml-2 text-xs ${socketConnected ? 'text-green-600' : 'text-red-600'}`}>
                    {socketConnected ? 'Live' : 'Offline'}
                  </span>
                </div>
              </>
            ) : (
              <div className="text-gray-400">Select a ticket to start chatting</div>
            )}
          </div>
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
            {selectedTicket ? (
              loadingMessages ? (
                <div className="flex items-center justify-center h-full">
                  <span className="text-gray-400">Loading...</span>
                </div>
              ) : messages.length === 0 ? (
                <div className="text-gray-400 text-center mt-12">No messages yet.</div>
              ) : (
                <div className="flex flex-col gap-4">
                  {messages
                    .slice()
                    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
                    .map((msg, idx) => (
                      <div
                        key={msg.id || idx}
                        className={`flex ${msg.is_admin ? 'justify-end' : 'justify-start'} w-full`}
                      >
                        <div className="flex items-end gap-2 max-w-full">
                          {!msg.is_admin && getAvatar(msg)}
                          <div
                            className={`max-w-[60%] rounded-2xl p-3 shadow ${
                              msg.is_admin
                                ? 'bg-green-100 text-green-900'
                                : msg.sender === 'bot'
                                ? 'bg-blue-50 text-blue-900'
                                : 'bg-white border text-gray-700'
                            }`}
                            style={{ wordBreak: 'break-word' }}
                          >
                            <div className="text-xs text-gray-500 mb-1">
                              {msg.is_admin ? 'Admin' : msg.sender === 'bot' ? 'Bot' : 'User'} • {new Date(msg.created_at).toLocaleString()}
                            </div>
                            <div>{msg.message}</div>
                          </div>
                          {msg.is_admin && getAvatar(msg)}
                        </div>
                      </div>
                    ))}
                  <div ref={messagesEndRef} />
                </div>
              )
            ) : (
              <div className="text-gray-400 text-center mt-12">Select a ticket to view messages.</div>
            )}
          </div>
          {/* Message Input */}
          {selectedTicket && (
            <div className="p-4 border-t bg-white flex items-center gap-2">
              <input
                type="text"
                value={reply}
                onChange={e => setReply(e.target.value)}
                placeholder="Type your reply..."
                className="input-field flex-1 rounded-lg border border-gray-300"
                onKeyDown={e => e.key === 'Enter' && handleSend()}
              />
              <button
                className="btn-primary flex items-center gap-1 rounded-lg px-4 py-2"
                onClick={handleSend}
                disabled={!reply.trim()}
                style={{ background: '#2563eb', color: 'white' }}
              >
                <Send className="h-4 w-4" />
                Send
              </button>
            </div>
          )}
        </div>

        {/* Details Panel */}
        <div className="w-80 border-l bg-gray-50 p-6 flex flex-col gap-4">
          {selectedTicket ? (
            <>
              <div className="flex items-center gap-3 mb-4">
                <User className="h-10 w-10 text-gray-700" />
                <div>
                  <div className="font-bold">{user?.name || selectedTicket.user_name}</div>
                  <div className="text-xs text-gray-500">{user?.email || selectedTicket.user_email}</div>
                  <div className="text-xs text-gray-400">{user?.phone || selectedTicket.user_phone}</div>
                  <div className="text-xs text-gray-400">{user?.role ? `Role: ${user.role}` : ''}</div>
                  <div className="text-xs text-gray-400">{user?.status ? `Status: ${user.status}` : ''}</div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-4 mb-4">
                <div className="font-semibold mb-1">Ticket Info</div>
                <div className="text-xs text-gray-500">ID: #{selectedTicket.id}</div>
                <div className="text-xs text-gray-500">Subject: {selectedTicket.subject}</div>
                <div className="text-xs text-gray-500">Category: {selectedTicket.category}</div>
                <div className="text-xs text-gray-500">Priority: {selectedTicket.priority}</div>
                <div className="text-xs text-gray-500">Status: {selectedTicket.status}</div>
                <div className="text-xs text-gray-500">Created: {selectedTicket.created_at ? new Date(selectedTicket.created_at).toLocaleString() : ''}</div>
              </div>
              <div className="flex gap-2 mt-2">
                <button className="btn-primary flex items-center gap-1 rounded-lg px-3 py-2" style={{ background: '#22c55e', color: 'white' }}>
                  <Check className="h-4 w-4" /> Mark Resolved
                </button>
                <button className="btn-secondary flex items-center gap-1 rounded-lg px-3 py-2" style={{ background: '#e0e7ef', color: '#2563eb' }}>
                  <Info className="h-4 w-4" /> View User Profile
                </button>
                <button className="btn-secondary flex items-center gap-1 rounded-lg px-3 py-2" style={{ background: '#f3f4f6', color: '#ef4444' }}>
                  <X className="h-4 w-4" /> Close Ticket
                </button>
              </div>
            </>
          ) : (
            <div className="text-gray-400">Select a ticket to view details.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Support;